import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Panel } from "@/components/Panel";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  usePurchaseOrders, useUnmatchedDocuments,
  useAssignDocument, useRejectDocument, useDeleteDocument, usePurchaseOrder,
  dokumentHerunterladen,
  type PurchaseOrderListRow, type PurchaseDocument,
} from "@/api/purchase";
import { formatUsd, orderStatusLabel, docTypLabel, dokumentLoeschHinweis } from "@/lib/purchase-format";
import { useAuth } from "@/api/auth";
import { bestellungAlsCsv, csvDateiname, csvHerunterladen } from "./csv";
import { NeueBestellungAssistent } from "./NeueBestellungAssistent";
import { MengenZelle } from "./board/MengenZelle";
import { EinkaufBoard, leseAnsicht, merkeAnsicht, type EinkaufAnsicht } from "./board/EinkaufBoard";
import { RollenPostfach } from "./RollenPostfach";
import { PdfGrundeinstellungen } from "./PdfGrundeinstellungen";
import { KapitalUebersicht } from "./KapitalUebersicht";
import { berechneKapital } from "./kapital";

const KEINE_BESTELLUNGEN: PurchaseOrderListRow[] = [];

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" }) : "—";

function etaLabel(row: PurchaseOrderListRow): string {
  if (!row.next_eta) return "—";
  return new Date(row.next_eta).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" });
}

/**
 * CSV-Ausgabe einer Bestellung aus der ÜBERSICHT heraus (A12).
 *
 * Die Liste traegt keine Positionen — die Akte laedt sie. Deshalb holt dieser Baustein die
 * Bestellung nach und loest den Download aus, sobald sie da ist. Der Riegel sorgt dafuer,
 * dass EIN Klick genau EINE Datei erzeugt: ohne ihn wuerde jedes erneute Rendern
 * (Fokuswechsel, Cache-Aktualisierung) eine weitere Datei ablegen.
 *
 * ⚠ Er wird nach jedem Lauf wieder GEOEFFNET. Die erste Fassung merkte sich die zuletzt
 * exportierte Bestell-Id dauerhaft — ein bewusster zweiter Export derselben Bestellung tat
 * dann nichts, ohne Meldung. Ein Riegel gegen Doppelausloesung darf kein Verbot der
 * Wiederholung werden. (Befund von Reviewer 1, angenommen.)
 */
function CsvAusUebersicht({ orderId, onFertig }: { orderId: number | null; onFertig: () => void }) {
  const { data, isError } = usePurchaseOrder(orderId);
  const { user } = useAuth();
  const erledigt = useRef<number | null>(null);

  useEffect(() => {
    if (orderId === null) {
      erledigt.current = null;   // Riegel wieder oeffnen — der naechste Klick zaehlt.
      return;
    }
    if (erledigt.current === orderId) return;
    if (isError) {
      erledigt.current = orderId;
      toast.error("Bestellung konnte nicht geladen werden");
      onFertig();
      return;
    }
    const order = data?.order;
    if (!order) return;
    erledigt.current = orderId;
    if (order.items.length === 0) {
      toast.error(`Bestellung ${order.order_number} hat keine Positionen`);
    } else {
      const csv = bestellungAlsCsv(order.items, !!user?.permissions?.article_purchase_prices);
      csvHerunterladen(csvDateiname(order.order_number), csv);
      toast.success(`CSV mit ${order.items.length} Positionen erstellt`);
    }
    onFertig();
  }, [orderId, data, isError, onFertig, user]);

  return null;
}

function OrderTable({ rows, muted, onOpen, onCsv }: {
  rows: PurchaseOrderListRow[];
  muted?: boolean;
  onOpen: (id: number) => void;
  onCsv: (id: number) => void;
}) {
  if (rows.length === 0) {
    return <div className="py-6 text-center text-sm text-ink-2">Keine Bestellungen.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-left text-xs text-ink-2">
            <th className="py-2 pr-3">Bestell-Nr.</th>
            <th className="py-2 pr-3">Lieferant</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2 pr-3 text-right" title="Positionen / Gesamtmenge">Pos. / Menge</th>
            <th className="py-2 pr-3 text-right">Rechnungssumme</th>
            <th className="py-2 pr-3 text-right">Bezahlt (USD)</th>
            <th className="py-2 pr-3 text-right">Container</th>
            <th className="py-2 pr-3">Nächste ETA</th>
            <th className="py-2 pr-3 text-right">Export</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => onOpen(row.id)}
              className={`cursor-pointer border-b border-line/50 hover:bg-panel-2 ${muted ? "text-ink-2" : "text-ink"}`}
            >
              <td className="py-2 pr-3 font-medium">
                <span className="inline-flex flex-wrap items-center gap-2">
                  {row.order_number}
                  {/* W4.3: Erinnerung — zugestellte Container ohne angelegten Wareneingang (nur offene Tabelle). */}
                  {!muted && row.we_pending_count > 0 && (
                    <span className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                      WE offen ({row.we_pending_count})
                    </span>
                  )}
                </span>
              </td>
              <td className="py-2 pr-3">{row.supplier_name ?? "—"}</td>
              <td className="py-2 pr-3">{orderStatusLabel(row.status)}</td>
              <td className="py-2 pr-3 text-right tabular-nums"><MengenZelle row={row} /></td>
              <td className="py-2 pr-3 text-right tabular-nums">
                {formatUsd(row.invoice_total_usd ?? row.positions_total_usd)}
                {row.invoice_total_usd == null && row.positions_total_usd != null && (
                  <span className="mt-0.5 block text-[11px] text-ink-2">Proforma</span>
                )}
              </td>
              <td className="py-2 pr-3 text-right">{formatUsd(row.paid_usd)}</td>
              <td className="py-2 pr-3 text-right">{row.shipment_count}</td>
              <td className="py-2 pr-3">{etaLabel(row)}</td>
              <td className="py-2 pr-3 text-right">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1 px-2 text-xs"
                  aria-label={`CSV für Bestellung ${row.order_number}`}
                  // Der Klick darf die Zeile nicht zugleich oeffnen — sonst landet der
                  // Nutzer in der Akte und die Datei erscheint „aus dem Nichts".
                  onClick={(e) => { e.stopPropagation(); onCsv(row.id); }}
                >
                  <Download className="size-3" /> CSV
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Summenzeile unter einer Tabelle — zeigt nur, was gezaehlt werden KANN. */
function Summenzeile({ rows }: { rows: PurchaseOrderListRow[] }) {
  if (rows.length === 0) return null;
  const rechnung = rows.reduce((s, r) => s + (r.invoice_total_usd ?? 0), 0);
  const bezahlt = rows.reduce((s, r) => s + (r.paid_usd ?? 0), 0);
  const ohnePreis = rows.filter((r) => r.invoice_total_usd === null).length;
  return (
    <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-1 border-t border-line pt-2 text-xs text-ink-2">
      <span>
        {rows.length} {rows.length === 1 ? "Bestellung" : "Bestellungen"}
      </span>
      <span>
        Rechnungssumme <span className="font-medium text-ink tabular-nums">{formatUsd(rechnung)}</span>
      </span>
      <span>
        Bezahlt <span className="font-medium text-ink tabular-nums">{formatUsd(bezahlt)}</span>
      </span>
      {/* Ohne diesen Hinweis liest sich eine Teilsumme wie eine Gesamtsumme. */}
      {ohnePreis > 0 && (
        <span className="text-amber-700 dark:text-amber-400">
          {ohnePreis} ohne Rechnungssumme — Summe unvollständig
        </span>
      )}
    </div>
  );
}

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data, isLoading, isError } = usePurchaseOrders();
  const { data: unmatchedDocs = [] } = useUnmatchedDocuments();
  const assignDoc = useAssignDocument();
  const rejectDoc = useRejectDocument();
  const deleteDoc = useDeleteDocument();

  const [isInboxOpen, setIsInboxOpen] = useState(false);
  const [assignTargetDoc, setAssignTargetDoc] = useState<PurchaseDocument | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedShipmentId, setSelectedShipmentId] = useState<number | null>(null);
  const [rejectDocId, setRejectDocId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState<string>("");
  const [deleteDocId, setDeleteDocId] = useState<number | null>(null);
  const [deleteReason, setDeleteReason] = useState<string>("");
  const deleteTargetDoc = unmatchedDocs.find((doc) => doc.id === deleteDocId) ?? null;
  const [downloadingDocId, setDownloadingDocId] = useState<number | null>(null);
  /** A10: stornierte Bestellungen sind standardmaessig ausgeblendet. */
  const [showCancelled, setShowCancelled] = useState(false);
  /** A12: Bestellung, deren CSV gerade geholt wird (null = keine). */
  const [csvOrderId, setCsvOrderId] = useState<number | null>(null);
  const csvFertig = useCallback(() => setCsvOrderId(null), []);
  /** A3: der Anlage-Assistent ersetzt das sofortige Anlegen einer leeren Bestellung. */
  const [assistentOffen, setAssistentOffen] = useState(false);
  /**
   * B5: Board oder Tabelle fuer die OFFENEN Bestellungen. Der Anfangswert kommt aus dem
   * Browser-Gedaechtnis (`leseAnsicht` als Initialisierer, damit er genau einmal laeuft);
   * jeder Wechsel schreibt ihn zurueck. Voreinstellung ist das Board — dort wird gearbeitet.
   */
  const [ansicht, setAnsicht] = useState<EinkaufAnsicht>(leseAnsicht);
  const darfPreise = !!user?.permissions?.article_purchase_prices;

  const { data: selectedOrderData } = usePurchaseOrder(selectedOrderId);
  const selectedOrderShipments = selectedOrderData?.order?.shipments ?? [];

  const orders = data?.orders ?? KEINE_BESTELLUNGEN;
  const kapital = useMemo(() => darfPreise
    ? new Map(berechneKapital(orders).bestellungen.map((row) => [row.id, row]))
    : undefined, [orders, darfPreise]);
  const open = orders.filter((o) => o.status !== "abgeschlossen" && o.status !== "storniert");
  // A10: „Abgeschlossen" und „Storniert" lagen in EINEM Block — eine stornierte Bestellung
  // sah damit aus wie eine erledigte. Jetzt getrennt: abgeschlossene sind Arbeitsergebnis
  // und bleiben sichtbar, stornierte sind zurueckgenommene Vorgaenge und liegen hinter einem
  // Schalter. Ausgeblendet heisst NICHT weg — der Schalter nennt ihre Zahl, sonst waere die
  // Voreinstellung ein stilles Verschwinden.
  const closed = orders.filter((o) => o.status === "abgeschlossen");
  const cancelled = orders.filter((o) => o.status === "storniert");


  return (
    <div className="min-w-0 max-w-full space-y-4">
      <CsvAusUebersicht orderId={csvOrderId} onFertig={csvFertig} />
      <NeueBestellungAssistent
        offen={assistentOffen}
        onClose={() => setAssistentOffen(false)}
        onAngelegt={(order) => {
          // Erst nach dem Anlegen in die Akte springen — vorher gab es dort nichts zu sehen.
          void queryClient.invalidateQueries({ queryKey: ["purchase"] });
          navigate(`/einkauf/${order.id}`);
        }}
      />
      {unmatchedDocs.length > 0 && (
        <div
          role="status"
          className="flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/20 dark:text-amber-200"
        >
          <div className="flex items-center gap-2 font-medium">
            <span>📎 Nicht zugeordnete Dokumente ({unmatchedDocs.length})</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-400 bg-white hover:bg-amber-100 dark:bg-amber-900/40 dark:hover:bg-amber-900/80"
            onClick={() => setIsInboxOpen(true)}
          >
            Inbox öffnen
          </Button>
        </div>
      )}

      {darfPreise && <KapitalUebersicht rows={orders} darfPreise={darfPreise} />}
      <RollenPostfach kapital={kapital} />

      <Panel
        title="Bestellungen"
        subtitle="Einkauf China — offene Bestellungen mit Zahlungsstand und nächster Ankunft"
        actions={
          <>
            {/* Zwei Knoepfe statt eines Schalters: beide Ansichten sind gleichwertig, und
                `aria-pressed` sagt vorlesenden Programmen, welche gerade gilt. */}
            <div className="flex items-center rounded-md border border-line p-0.5">
              {(["board", "tabelle"] as const).map((a) => (
                <button
                  key={a}
                  type="button"
                  aria-pressed={ansicht === a}
                  onClick={() => { setAnsicht(a); merkeAnsicht(a); }}
                  className={`rounded px-2 py-1 text-xs ${
                    ansicht === a ? "bg-background font-medium text-ink" : "text-ink-2"
                  }`}
                >
                  {a === "board" ? "Board" : "Tabelle"}
                </button>
              ))}
            </div>
            <PdfGrundeinstellungen />
            <Button size="sm" className="gap-1.5" onClick={() => setAssistentOffen(true)}>
              <Plus className="size-3.5" /> Neue Bestellung
            </Button>
          </>
        }
      >
        {isError ? (
          <div className="py-6 text-center text-sm text-coral">
            Bestellungen nicht erreichbar — fehlt die Einkaufs-Berechtigung?
          </div>
        ) : isLoading ? (
          <div className="py-6 text-center text-sm text-ink-2">Lädt…</div>
        ) : (
          ansicht === "board" ? (
            <EinkaufBoard
              rows={open}
              onOpen={(id) => navigate(`/einkauf/${id}`)}
              onCsv={setCsvOrderId}
              darfPreise={darfPreise}
            />
          ) : (
            <OrderTable rows={open} onOpen={(id) => navigate(`/einkauf/${id}`)} onCsv={setCsvOrderId} />
          )
        )}
      </Panel>

      <Panel title="Abgeschlossen" subtitle="Erledigte Bestellungen mit Summen">
        {isError ? (
          <div className="py-6 text-center text-sm text-coral">
            Bestellungen nicht erreichbar — fehlt die Einkaufs-Berechtigung?
          </div>
        ) : isLoading ? (
          <div className="py-6 text-center text-sm text-ink-2">Lädt…</div>
        ) : (
          <>
            <OrderTable rows={closed} muted onOpen={(id) => navigate(`/einkauf/${id}`)} onCsv={setCsvOrderId} />
            <Summenzeile rows={closed} />
          </>
        )}
      </Panel>

      {!isError && !isLoading && cancelled.length > 0 && (
        <Panel
          title="Storniert"
          subtitle="Zurückgenommene Bestellungen — zählen nirgends mit"
          actions={
            <Button
              size="sm"
              variant="outline"
              aria-expanded={showCancelled}
              onClick={() => setShowCancelled((v) => !v)}
            >
              {showCancelled ? "Stornierte ausblenden" : `Stornierte anzeigen (${cancelled.length})`}
            </Button>
          }
        >
          {showCancelled ? (
            <OrderTable rows={cancelled} muted onOpen={(id) => navigate(`/einkauf/${id}`)} onCsv={setCsvOrderId} />
          ) : (
            <div className="py-4 text-center text-sm text-ink-2">
              {cancelled.length} {cancelled.length === 1 ? "stornierte Bestellung" : "stornierte Bestellungen"} ausgeblendet.
            </div>
          )}
        </Panel>
      )}

      <Dialog open={isInboxOpen} onOpenChange={setIsInboxOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Dokumente-Inbox</DialogTitle>
            <DialogDescription>
              Nicht zugeordnete Dokumente einsehen und einer Bestellung zuweisen
            </DialogDescription>
          </DialogHeader>

          {unmatchedDocs.length === 0 ? (
            <div className="py-6 text-center text-sm text-ink-2">
              Keine unzugeordneten Dokumente vorhanden.
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-xs text-ink-2">
                    <th className="py-2 pr-3">Datei</th>
                    <th className="py-2 pr-3">Typ</th>
                    <th className="py-2 pr-3">Datum</th>
                    <th className="py-2 pr-3 text-right">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {unmatchedDocs.map((doc) => (
                    <tr key={doc.id} className="border-b border-line/50 hover:bg-panel-2">
                      <td className="py-2 pr-3 font-medium text-ink">
                        {doc.file_name || doc.sharepoint_path || `Dokument #${doc.id}`}
                      </td>
                      <td className="py-2 pr-3">{doc.typ ? docTypLabel(doc.typ) : "—"}</td>
                      <td className="py-2 pr-3 text-xs text-ink-2">{fmtDate(doc.created_at)}</td>
                      <td className="py-2 pr-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            title="Dokument herunterladen"
                            aria-label={`Dokument ${doc.file_name || doc.id} herunterladen`}
                            disabled={downloadingDocId === doc.id}
                            onClick={async () => {
                              setDownloadingDocId(doc.id);
                              try {
                                await dokumentHerunterladen(doc.id, doc.file_name || `dokument-${doc.id}.pdf`);
                              } catch (err) {
                                toast.error(err instanceof Error ? err.message : "Download fehlgeschlagen");
                              } finally {
                                setDownloadingDocId(null);
                              }
                            }}
                          >
                            {downloadingDocId === doc.id ? (
                              <Loader2 className="size-3.5 animate-spin" />
                            ) : (
                              <Download className="size-3.5" />
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => {
                              setAssignTargetDoc(doc);
                              setSelectedOrderId(open[0]?.id ?? null);
                              setSelectedShipmentId(null);
                            }}
                          >
                            Zuordnen…
                          </Button>
                            {doc.agent_status === "extrahiert" && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs text-red-600 hover:text-red-700 dark:text-red-400"
                                onClick={() => {
                                  setRejectDocId(doc.id);
                                  setRejectReason("");
                                }}
                              >
                                Ablehnen
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs text-red-600 hover:text-red-700 dark:text-red-400"
                              onClick={() => {
                                setDeleteDocId(doc.id);
                                setDeleteReason("");
                              }}
                            >
                              Löschen
                            </Button>
                          </div>
                        </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={assignTargetDoc !== null} onOpenChange={(open) => { if (!open) setAssignTargetDoc(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Dokument zuordnen</DialogTitle>
            <DialogDescription>
              Wähle eine offene Bestellung und optional einen Container für {assignTargetDoc?.file_name || `Dokument #${assignTargetDoc?.id}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <label className="text-xs font-medium text-ink">Bestellung</label>
              <select
                value={selectedOrderId ?? ""}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setSelectedOrderId(Number.isSafeInteger(val) && val > 0 ? val : null);
                  setSelectedShipmentId(null);
                }}
                className="w-full rounded border border-line bg-background px-3 py-1.5 text-xs text-ink"
              >
                <option value="">-- Bestellung wählen --</option>
                {open.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.order_number} ({o.supplier_name ?? "Ohne Lieferant"})
                  </option>
                ))}
              </select>
            </div>

            {selectedOrderId ? (
              <div className="space-y-1">
                <label className="text-xs font-medium text-ink">Container (optional)</label>
                <select
                  value={selectedShipmentId ?? ""}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setSelectedShipmentId(Number.isSafeInteger(val) && val > 0 ? val : null);
                  }}
                  className="w-full rounded border border-line bg-background px-3 py-1.5 text-xs text-ink"
                >
                  <option value="">Kein Container (nur Bestellung)</option>
                  {selectedOrderShipments.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.container_no ? `Container ${s.container_no}` : s.label ? s.label : `Container #${s.id}`}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setAssignTargetDoc(null)}>
              Abbrechen
            </Button>
            <Button
              size="sm"
              disabled={assignDoc.isPending || !selectedOrderId}
              onClick={() => {
                if (!assignTargetDoc || !selectedOrderId) return;
                assignDoc.mutate(
                  { docId: assignTargetDoc.id, orderId: selectedOrderId, shipmentId: selectedShipmentId },
                  {
                    onSuccess: () => {
                      toast.success("Dokument zugeordnet");
                      setAssignTargetDoc(null);
                    },
                    onError: () => toast.error("Zuordnung fehlgeschlagen"),
                  }
                );
              }}
            >
              Zuordnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectDocId !== null} onOpenChange={(open) => { if (!open) setRejectDocId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Dokument ablehnen</DialogTitle>
            <DialogDescription>
              Bitte gib einen Grund für die Ablehnung des Dokuments an.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Textarea
              placeholder="Grund für Ablehnung (z. B. falsche Summen, unlesbar...)"
              maxLength={500}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
            />
            <div className="text-right text-xs text-ink-2">
              {rejectReason.length}/500
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setRejectDocId(null)}>
              Abbrechen
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={rejectDoc.isPending || !rejectReason.trim()}
              onClick={() => {
                if (!rejectDocId) return;
                rejectDoc.mutate(
                  { docId: rejectDocId, grund: rejectReason.trim() },
                  {
                    onSuccess: () => {
                      toast.success("Dokument abgelehnt");
                      setRejectDocId(null);
                    },
                    onError: () => toast.error("Ablehnung fehlgeschlagen"),
                  }
                );
              }}
            >
              Dokument ablehnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDocId !== null} onOpenChange={(open) => { if (!open) setDeleteDocId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Dokument löschen</DialogTitle>
            <DialogDescription>
              {dokumentLoeschHinweis(deleteTargetDoc?.sharepoint_path ?? null)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Textarea
              placeholder="Grund für Löschung (z. B. versehentlich hochgeladen)"
              maxLength={500}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              rows={4}
            />
            <div className="text-right text-xs text-ink-2">
              {deleteReason.length}/500
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setDeleteDocId(null)}>
              Abbrechen
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={deleteDoc.isPending || !deleteReason.trim()}
              onClick={() => {
                if (!deleteDocId) return;
                deleteDoc.mutate(
                  { docId: deleteDocId, grund: deleteReason.trim() },
                  {
                    onSuccess: () => {
                      toast.success("Dokument gelöscht");
                      setDeleteDocId(null);
                    },
                    onError: () => toast.error("Löschen fehlgeschlagen"),
                  }
                );
              }}
            >
              Dokument löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
